import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useJoinTeam } from '@/hooks/useUserManagement'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react'
import { Link } from 'react-router-dom'

export function JoinTeam() {
  const navigate = useNavigate()
  const [inviteCode, setInviteCode] = useState('')
  const [success, setSuccess] = useState(false)

  const joinTeam = useJoinTeam()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (inviteCode.trim()) {
      try {
        await joinTeam.mutateAsync(inviteCode.trim())
        setSuccess(true)
        setTimeout(() => {
          navigate('/')
        }, 2000)
      } catch (error) {
        console.error('Failed to join team:', error)
      }
    }
  }

  if (success) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
        </div>

        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">
            Successfully Joined!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your membership request has been submitted and is pending approval.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link
          to="/"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Join Team</h1>
        <p className="mt-1 text-sm text-gray-500">
          Enter the invite code to join a team
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="invite-code" className="block text-sm font-medium text-gray-700">
                Invite Code
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="invite-code"
                  id="invite-code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="Enter invite code"
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Ask your team admin for the invite code
              </p>
            </div>

            {joinTeam.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      {joinTeam.error.message || 'Failed to join team'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!inviteCode.trim() || joinTeam.isPending}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {joinTeam.isPending ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Joining...</span>
                  </>
                ) : (
                  'Join Environment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
